/**
 * Every path decided in docs/ui/routes.md, as named constants instead of
 * strings scattered through route configs, `<Link>`s, and redirects. The
 * four groups mirror that document's access tiers exactly — Guest-only is
 * kept separate from Public because the two have different redirect
 * behavior (see routes.md's Redirect rules), not because they look
 * different.
 *
 * Only the path table is encoded here. The router itself
 * (src/routes/router.tsx) registers real routes incrementally as each page
 * is built — this file exists so that wiring, when it happens, references
 * one agreed string per path instead of every feature re-typing it.
 */
export const PUBLIC_ROUTES = {
  home: "/",
  search: "/search",
  productDetails: "/product/:slug",
  helpCenter: "/help",
  helpArticle: "/help/:articleSlug",
  /** Read-only view of *someone else's* profile — public, since viewing is
   * open to anyone (the same split `productDetails` already has between
   * public viewing and auth-gated actions). Editing your own profile is
   * the separate, protected `profile` route below — this one never takes
   * a self-referencing id. */
  sellerProfile: "/profile/:userId",
} as const;

export const GUEST_ONLY_ROUTES = {
  login: "/login",
  register: "/register",
  otpVerification: "/otp-verification",
  forgotPassword: "/forgot-password",
  resetPassword: "/reset-password/:token",
} as const;

export const PROTECTED_ROUTES = {
  myListings: "/my-listings",
  wishlist: "/wishlist",
  chat: "/chat",
  chatConversation: "/chat/:conversationId",
  notifications: "/notifications",
  profile: "/profile",
  settings: "/settings",
  sellMedia: "/sell/media",
  sellDetails: "/sell/details",
  sellFinalize: "/sell/finalize",
  editListing: "/listings/:id/edit",
} as const;

export const ADMIN_ROUTES = {
  dashboard: "/admin",
  users: "/admin/users",
  listings: "/admin/listings",
  verifications: "/admin/verifications",
  settings: "/admin/settings",
  reports: "/admin/reports",
} as const;

export const ROUTES = {
  ...PUBLIC_ROUTES,
  ...GUEST_ONLY_ROUTES,
  ...PROTECTED_ROUTES,
  ...ADMIN_ROUTES,
} as const;
